import { addError } from "@wayflyer/blackbox-fe";
import { WayflyerSchemas } from "api-types";
import { createContext, useCallback, useContext, useState } from "react";
import { requestPost, useAnalyticsContext } from "shared-utils";

import { analytics } from "../analytics";
import type { ModalConfig } from "../components/invite-modal/types";

export type InviteContextValue = {
  inviteHandler: (email: string, roles?: WayflyerSchemas.CompanyRole[], onSuccess?: () => void) => Promise<void>;
  isModalOpen: boolean;
  openModal: (
    onCloseCallback?: () => void,
    onOpenCallback?: () => void,
    onSuccessCallback?: () => void,
    modalConfig?: ModalConfig
  ) => void;
  closeModal: () => void;
  onSuccessCallback?: (() => void) | undefined;
  modalConfig?: ModalConfig;
};

export interface InviteContextProps {
  homePath: string;
  children: any;
}

const InviteContext = createContext<InviteContextValue>({
  inviteHandler: () => Promise.reject("Something went wrong, please contact support"),
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
});

const errorMap = {
  "Email already exists": "Invite - Invitation error - Email already exists",
  "Rate limit reached": "Invite - Invitation error - Rate limit reached",
  "internal server error": "Something went wrong, please contact support",
  "Invalid email": "Invite - Invitation error - Invalid email",
};

const sendInvite = async (homePath: string, email: string, roles) => {
  try {
    if (!homePath.length) {
      throw Error("No homePath available when attempting to send invite to: " + email);
    }
    await requestPost(`${homePath}invite/`, { email, roles });
  } catch (error) {
    addError(error);
    throw errorMap[error as string] || "Invite - Invitation error";
  }
};

export function InviteProvider({ homePath, children }: InviteContextProps) {
  const analyticsContext = useAnalyticsContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>();
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>();
  const [modalConfig, setModalConfig] = useState<ModalConfig | undefined>(undefined);

  const inviteHandler = useCallback(
    (email: string, roles?: WayflyerSchemas.CompanyRole[], onSuccess?: () => void) => {
      analytics.inviteMembersSendClicked(analyticsContext);
      return sendInvite(homePath, email, roles).then(() => {
        if (onSuccess) {
          onSuccess();
        }
      });
    },
    [analyticsContext, homePath]
  );

  const openModal = useCallback(
    (
      onCloseCallback?: () => void,
      onOpenCallback?: () => void,
      onSuccessCallback?: () => void,
      modalConfig?: ModalConfig
    ) => {
      if (onOpenCallback) {
        onOpenCallback();
      }
      setOnCloseCallback(() => onCloseCallback);
      setOnSuccessCallback(() => onSuccessCallback);
      setModalConfig(modalConfig);
      setIsModalOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(undefined);
    }
    setOnSuccessCallback(undefined);
    setModalConfig(undefined);
  }, [onCloseCallback]);

  return (
    <InviteContext.Provider
      value={{
        inviteHandler,
        isModalOpen,
        openModal,
        closeModal,
        onSuccessCallback,
        modalConfig,
      }}
    >
      {typeof children === "function" ? children({ inviteHandler }) : children}
    </InviteContext.Provider>
  );
}

export const useInviteHandler = (): InviteContextValue => useContext(InviteContext);

export const useInviteModal = () => {
  const context = useContext(InviteContext);
  return {
    isOpen: context.isModalOpen,
    openModal: context.openModal,
    closeModal: context.closeModal,
    modalConfig: context.modalConfig,
  };
};

export const useInviteModalActions = () => {
  const { openModal, closeModal } = useInviteModal();

  return {
    openInviteModal: openModal,
    closeInviteModal: closeModal,
  };
};
